import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Boxes,
  Check,
  Gem,
  ImagePlus,
  Lock,
  MoreHorizontal,
  Palette,
  Pin,
  ShoppingBag,
  type LucideIcon,
} from 'lucide-react';
import { Button, Modal } from '@/components/ui';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/config/constants';
import { useAuth } from '@/app/providers/AuthProvider';
import { useAuthModal } from '@/features/auth/authModalStore';
import { THEMES } from '@/features/theme/themes';
import { themeAssetExists } from '@/features/theme/themeAssets';
import { useThemeSelection } from '@/features/theme/useThemeSelection';
import { THEME_LINEUP_MAX, useThemeLineup } from '@/features/theme/themeLineup';
import { ThemeThumbnail } from '@/features/theme/components/ThemeThumbnail';
import {
  DEFAULT_EMBLEM_ID,
  DEFAULT_TITLE_ID,
  EMBLEMS,
  getTitleFamilies,
  isUnlocked,
  unlockLabel,
} from '../cosmetics';
import { useUpdateProfileCosmetic } from '../hooks/useProfile';
import { EmblemGlyph } from './EmblemGlyph';

type LockerTab = 'themes' | 'titles' | 'emblems';

const TABS: { id: LockerTab; label: string; icon: LucideIcon }[] = [
  { id: 'themes', label: 'Themes', icon: Palette },
  { id: 'titles', label: 'Titles', icon: Award },
  { id: 'emblems', label: 'Emblems', icon: Gem },
];

/**
 * "Your Locker" — one overlay that holds everything a user can equip: background
 * themes, title cards, and emblems. It's a portal modal so it has its own
 * sizing and never affects the profile page or settings layout. Theme picks
 * persist via the shared theme system; title/emblem picks persist to the
 * `profiles` row (mirrors the OG `saveProfileCosmetic`).
 */
export function Locker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<LockerTab>('themes');
  const navigate = useNavigate();

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="max-w-3xl border border-white/15 bg-[linear-gradient(145deg,rgba(255,255,255,0.16),rgba(84,185,254,0.08)_45%,rgba(8,10,18,0.32))] shadow-[0_28px_90px_rgba(0,0,0,0.42)] backdrop-blur-2xl"
    >
      <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" />
      <div className="mb-5 flex items-center justify-between gap-3 pr-8">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/[0.08] text-accent shadow-[0_10px_30px_rgba(84,185,254,0.18)]">
            <Boxes size={20} strokeWidth={2} />
          </span>
          <div>
            <h2 className="text-lg font-extrabold leading-tight text-hi">Your Locker</h2>
            <p className="text-xs text-mid">Themes, titles, and emblems for your profile.</p>
          </div>
        </div>
        <button
          type="button"
          title="Open the Marketplace"
          onClick={() => {
            onClose();
            navigate(ROUTES.marketplace);
          }}
          className="inline-flex flex-none items-center gap-1.5 rounded-pill border border-white/15 bg-white/[0.08] px-3 py-1.5 text-[12px] font-bold text-hi transition-all hover:bg-white/[0.13] active:scale-95"
        >
          <ShoppingBag size={13} strokeWidth={2.2} />
          Marketplace
        </button>
      </div>

      <div className="mb-5 flex gap-1 rounded-pill border border-white/15 bg-white/[0.06] p-1 shadow-inner shadow-white/[0.03]">
        {TABS.map(({ id, label, icon: Icon }) => {
          const on = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              aria-pressed={on}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-pill px-3 py-2 text-[12px] font-bold transition-all duration-200',
                on
                  ? 'bg-accent text-black shadow-[0_4px_14px_var(--winr-accent-soft)]'
                  : 'text-mid hover:bg-white/[0.04] hover:text-hi',
              )}
            >
              <Icon size={13} strokeWidth={2.2} />
              {label}
            </button>
          );
        })}
      </div>

      <div className="max-h-[60vh] overflow-y-auto pr-1">
        {tab === 'themes' && <ThemesPanel />}
        {tab === 'titles' && <TitlesPanel />}
        {tab === 'emblems' && <EmblemsPanel />}
      </div>
    </Modal>
  );
}

function ThemesPanel() {
  const { activeId, pick, onUpload } = useThemeSelection();
  const lineup = useThemeLineup((s) => s.lineup);
  const toggle = useThemeLineup((s) => s.toggle);
  const full = lineup.length >= THEME_LINEUP_MAX;

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[11px] text-mid">
          Tap to apply · <Pin size={10} className="inline -translate-y-px text-accent" /> pin to
          your quick menu
        </p>
        <span className="flex-none text-[11px] font-bold text-lo">
          Lineup {lineup.length}/{THEME_LINEUP_MAX}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {THEMES.map((t) => {
          const unavailable = t.file != null && !themeAssetExists(t.file);
          const active = t.id === activeId;
          const pinned = lineup.includes(t.id);
          return (
            <div
              key={t.id}
              className={cn(
                'group relative aspect-[16/9] overflow-hidden rounded-xl border transition-all duration-200 ease-cinematic',
                unavailable
                  ? 'border-card-line opacity-50'
                  : 'border-card-line hover:-translate-y-0.5 hover:border-white/30',
                active && 'border-accent shadow-[0_0_0_2px_var(--winr-accent-soft)]',
              )}
            >
              <button
                type="button"
                disabled={unavailable}
                onClick={() => pick(t.id)}
                aria-pressed={active}
                title={unavailable ? `${t.name} — image not added yet` : t.name}
                className={cn(
                  'absolute inset-0',
                  unavailable ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
              >
                <ThemeThumbnail theme={t} className="absolute inset-0" />
                <span className="absolute inset-0 bg-gradient-to-b from-transparent to-black/65" />
                <span className="absolute bottom-1.5 left-2 text-[11px] font-bold text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]">
                  {t.name}
                </span>
              </button>

              {/* Pin toggle — adds/removes this theme from the avatar quick menu. */}
              {!unavailable && (
                <button
                  type="button"
                  onClick={() => toggle(t.id)}
                  disabled={!pinned && full}
                  aria-pressed={pinned}
                  title={
                    pinned
                      ? 'Remove from quick menu'
                      : full
                        ? 'Lineup full (8)'
                        : 'Pin to quick menu'
                  }
                  className={cn(
                    'absolute left-1.5 top-1.5 z-10 grid h-[22px] w-[22px] place-items-center rounded-full border transition-all active:scale-90',
                    pinned
                      ? 'border-accent bg-accent text-black'
                      : 'border-white/20 bg-black/45 text-white/85 hover:bg-black/65',
                    !pinned && full && 'cursor-not-allowed opacity-40',
                  )}
                >
                  <Pin size={11} strokeWidth={2.4} className={pinned ? 'fill-current' : ''} />
                </button>
              )}

              {active && (
                <span className="absolute right-1.5 top-1.5 z-10 grid h-[18px] w-[18px] place-items-center rounded-full bg-accent text-black shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
                  <Check size={11} strokeWidth={3} />
                </span>
              )}
            </div>
          );
        })}

        <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-card-line bg-white/[0.02] text-lo transition-colors hover:text-hi">
          <input
            type="file"
            accept="image/png,image/jpeg"
            className="sr-only"
            onChange={onUpload}
          />
          <ImagePlus size={18} strokeWidth={1.8} />
          <span className="text-[11px] font-bold">Upload</span>
        </label>
      </div>
    </>
  );
}

/** Shared title/emblem persistence — mirrors ProfilePage's save + refresh. */
function useCosmeticSave() {
  const { profile, refreshProfile } = useAuth();
  const update = useUpdateProfileCosmetic();
  const save = (patch: { title_id?: string; emblem_id?: string }) => {
    if (!profile) return;
    update.mutate({ id: profile.id, patch }, { onSuccess: () => void refreshProfile() });
  };
  return { profile, save };
}

function TitlesPanel() {
  const { profile, save } = useCosmeticSave();
  const [previewByFamily, setPreviewByFamily] = useState<Record<string, string>>({});
  // Which family's variant popover is open (only one at a time).
  const [openFamily, setOpenFamily] = useState<string | null>(null);
  if (!profile) return <LockedOut kind="titles" />;
  const selectedId = profile.title_id ?? DEFAULT_TITLE_ID;
  const families = getTitleFamilies();
  const displayName = profile.display_name ?? 'WINR';

  return (
    <>
      <p className="mb-3 text-[11px] text-mid">These are for your profile.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {families.map((family) => {
          const firstTitle = family[0];
          if (!firstTitle) return null;
          const familyKey = family.map((title) => title.id).join('-');
          const selectedVariant = family.find((title) => title.id === selectedId);
          const previewId = previewByFamily[familyKey] ?? selectedVariant?.id ?? firstTitle.id;
          const title = family.find((item) => item.id === previewId) ?? firstTitle;
          const selected = family.some((item) => item.id === selectedId);
          const hasVariants = family.length > 1;
          const open = openFamily === familyKey;

          return (
            // Wider 16/9 frame shows the full length of the cinematic banner; the
            // outer wrapper isn't clipped so the variant popover can float above it.
            <div key={familyKey} className="relative">
              <div
                className={cn(
                  'group relative aspect-[16/9] overflow-hidden rounded-xl border transition-all duration-200 ease-cinematic',
                  selected
                    ? 'border-accent shadow-[0_0_0_2px_var(--winr-accent-soft)]'
                    : 'border-card-line hover:-translate-y-0.5 hover:border-white/30',
                )}
              >
                <button
                  type="button"
                  onClick={() => save({ title_id: title.id })}
                  aria-pressed={selected}
                  className="absolute inset-0 cursor-pointer"
                >
                  <span
                    className={cn(
                      'absolute inset-0 bg-cover bg-center',
                      title.anim === 'kenBurns' && 'animate-ken-burns',
                    )}
                    style={{ backgroundImage: `url('${title.bg}')` }}
                  />
                  <span className="absolute inset-0 bg-gradient-to-b from-transparent to-black/65" />
                  <span className="absolute bottom-2 left-2.5 text-[12px] font-bold text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]">
                    {displayName}
                  </span>
                </button>

                {selected && (
                  <span className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-pill bg-accent px-2 py-0.5 text-[10px] font-bold text-black shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
                    <Check size={11} strokeWidth={3} /> Equipped
                  </span>
                )}

                {hasVariants && (
                  <button
                    type="button"
                    onClick={() => setOpenFamily(open ? null : familyKey)}
                    aria-expanded={open}
                    title={`${family.length} variants`}
                    className={cn(
                      'absolute bottom-2 right-2 z-10 inline-flex items-center gap-1 rounded-pill border px-2 py-1 text-[10px] font-bold backdrop-blur-md transition-all active:scale-95',
                      open
                        ? 'border-accent bg-accent text-black'
                        : 'border-white/15 bg-black/55 text-white/85 hover:bg-black/70',
                    )}
                  >
                    <MoreHorizontal size={14} strokeWidth={2.6} />
                    {family.length}
                  </button>
                )}
              </div>

              {/* Variant popover — inline-tip reveal of full-length previews. */}
              {hasVariants && open && (
                <>
                  <div className="fixed inset-0 z-[40]" onClick={() => setOpenFamily(null)} />
                  <div className="absolute bottom-[calc(100%+8px)] right-0 z-50 w-[260px] origin-bottom-right animate-fade-in-up rounded-xl border border-white/15 bg-[rgba(16,16,22,0.96)] p-2 shadow-[0_22px_55px_rgba(0,0,0,0.55)] backdrop-blur-2xl">
                    <div className="px-1 pb-1.5 pt-0.5 text-[10px] font-bold uppercase tracking-[0.6px] text-lo">
                      Variants
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {family.map((variant) => {
                        const active = variant.id === selectedId;
                        return (
                          <button
                            key={variant.id}
                            type="button"
                            onClick={() => {
                              setPreviewByFamily((current) => ({ ...current, [familyKey]: variant.id }));
                              save({ title_id: variant.id });
                            }}
                            aria-pressed={active}
                            className={cn(
                              'relative h-16 w-full overflow-hidden rounded-lg border bg-black/50 transition-all',
                              active
                                ? 'border-accent shadow-[0_0_0_1px_var(--winr-accent-soft)]'
                                : 'border-white/12 opacity-90 hover:opacity-100 hover:border-white/30',
                            )}
                          >
                            {/* contain → the whole banner is visible, never cropped */}
                            <span
                              className="absolute inset-0 bg-contain bg-center bg-no-repeat"
                              style={{ backgroundImage: `url('${variant.bg}')` }}
                            />
                            <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/80 to-transparent px-2 py-1">
                              <span className="truncate text-[11px] font-bold text-white">
                                {variant.name}
                              </span>
                              {active && <Check size={12} strokeWidth={3} className="flex-none text-accent" />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function EmblemsPanel() {
  const { profile, save } = useCosmeticSave();
  if (!profile) return <LockedOut kind="emblems" />;
  const selectedId = profile.emblem_id ?? DEFAULT_EMBLEM_ID;

  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
      {EMBLEMS.map((e) => {
        const selected = e.id === selectedId;
        const unlocked = isUnlocked(e.unlock);
        return (
          <button
            key={e.id}
            type="button"
            disabled={!unlocked}
            onClick={() => unlocked && save({ emblem_id: e.id })}
            className={cn(
              'glass relative flex flex-col items-center gap-2 rounded-xl p-4 transition-transform',
              selected ? 'border-accent' : 'border-card-line',
              unlocked ? 'hover:-translate-y-0.5' : 'cursor-not-allowed opacity-60',
            )}
          >
            <EmblemGlyph emblem={e} size={30} />
            <div className="text-[11px] font-semibold text-hi">{e.name}</div>
            {selected ? (
              <span className="inline-flex items-center gap-1 rounded-pill bg-accent px-2 py-0.5 text-[10px] font-bold text-black">
                <Check size={11} /> Selected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-lo">
                {!unlocked && <Lock size={10} />}
                {unlockLabel(e.unlock)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function LockedOut({ kind }: { kind: string }) {
  const openAuth = useAuthModal((s) => s.openAuth);
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-white/[0.04] text-lo">
        <Lock size={18} />
      </span>
      <p className="text-sm text-mid">Log in to claim and equip {kind}.</p>
      <Button size="sm" onClick={openAuth}>
        Log in
      </Button>
    </div>
  );
}
